import { useQuery } from '@apollo/client/react';
import { useFocusEffect, useRouter } from 'expo-router';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshControl, SectionList, type SectionListData, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';

import { PrimaryButton } from '@/components/atoms/button/PrimaryButton';
import { ErrorState, LoadingState } from '@/components/atoms/state';
import { PaginationFooter } from '@/components/molecules/list/PaginationFooter';
import { MeetingMemoEmptyIllustration } from '@/components/atoms/illustrations/MeetingMemoEmptyIllustration';
import { FilterDropdownChip } from '@/components/molecules/filter';
import { UserPickerSheet, type UserOption } from '@/components/molecules/person-in-charge';
import { USER_PICKER_GET_USERS_VARIABLES } from '@/codegen/usermobile-bff/users/userPickerGetUsersVariables';
import { SectionDateHeader } from '@/components/molecules/section-header/SectionDateHeader';
import { ConfirmDialog } from '@/components/atoms/dialog';
import { StartRecordingNote } from '@/components/molecules/voice-recording';
import { formatMeetingDate } from '@/formatters/formatMeetingDate';
import {
  GetMeetingsDocument,
  GetUsersDocument,
  MeDocument,
  type GetMeetingsQuery,
} from '@/generated/usermobile-bff/graphql';
import { useAiTranscriptionConsentDialog, usePaginatedFetchMore, useRefreshHandler } from '@/hooks';
import { useTranslation } from '@/i18n';
import { spacing, unisonColors, useAppTheme } from '@/theme';
import { useVoiceRecordingStore } from '@/stores/useVoiceRecordingStore';

import { MeetingListItem } from './MeetingListItem';

const PER_PAGE = 20;

const FILTER_ME = 'me' as const;

interface MeetingListProps {
  readonly contactId?: string;
  readonly showFilter?: boolean;
  readonly onAttentionChange?: (hasAttentionItems: boolean) => void;
}

interface MeetingSection {
  readonly title: string;
  readonly data: readonly GetMeetingsQuery['meetings']['data'][number][];
}

interface MeetingListRowProps {
  readonly item: GetMeetingsQuery['meetings']['data'][number];
  readonly pendingUploadMeetingIds: readonly string[];
  readonly failedUploadMeetingIds: readonly string[];
  readonly onPressMeeting: (meetingId: string) => void;
}

interface MeetingListHeaderProps {
  readonly chipLabel: string;
  readonly chipValue: string;
  readonly onChipPress: () => void;
  readonly showFilter: boolean;
}

const MeetingListRowBase = ({
  item,
  pendingUploadMeetingIds,
  failedUploadMeetingIds,
  onPressMeeting,
}: MeetingListRowProps) => {
  const handlePress = useCallback(() => {
    onPressMeeting(item.id);
  }, [item.id, onPressMeeting]);

  const hasPendingUpload = pendingUploadMeetingIds.includes(item.id);
  const hasFailedUpload = failedUploadMeetingIds.includes(item.id);

  return (
    <MeetingListItem
      hasFailedUpload={hasFailedUpload}
      hasPendingUpload={hasPendingUpload}
      item={item}
      onPress={handlePress}
    />
  );
};

const MeetingListRow = memo(MeetingListRowBase);

const ItemSeparator = () => <View style={meetingListStyles.itemSeparator} />;

const MeetingListHeaderBase = ({
  chipLabel,
  chipValue,
  onChipPress,
  showFilter,
}: MeetingListHeaderProps) => {
  if (!showFilter) {
    return null;
  }

  return (
    <View style={meetingListStyles.filterRow}>
      <FilterDropdownChip
        label={chipLabel}
        selected={chipValue.length > 0}
        value={chipValue}
        testID="meeting-filter-chip"
        onPress={onChipPress}
      />
    </View>
  );
};

const MeetingListHeader = memo(MeetingListHeaderBase);

interface EmptyStateProps {
  readonly title: string;
  readonly description: string;
  readonly actionLabel: string;
  readonly onAction: () => void;
}

const EmptyState = ({ title, description, actionLabel, onAction }: EmptyStateProps) => (
  <View
    style={{
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingBottom: 16,
    }}>
    <MeetingMemoEmptyIllustration />
    <View style={{ gap: 9, alignItems: 'center', paddingHorizontal: 16, marginTop: 16 }}>
      <Text variant="titleLarge" style={{ textAlign: 'center' }}>
        {title}
      </Text>
      <Text variant="bodyMedium" style={{ textAlign: 'center', maxWidth: 352, color: '#49454F' }}>
        {description}
      </Text>
    </View>
    <View style={{ paddingHorizontal: 16, paddingVertical: 12, width: 332 }}>
      <PrimaryButton testID="empty-state-action" onPress={onAction}>
        {actionLabel}
      </PrimaryButton>
    </View>
  </View>
);

const pendingUploadMeetingIdsSelector = (state: {
  upload: { pendingUploadMeetingIds: readonly string[] };
}) => state.upload.pendingUploadMeetingIds;

const failedUploadMeetingIdsSelector = (state: {
  upload: { failedUploadMeetingIds: readonly string[] };
}) => state.upload.failedUploadMeetingIds;

const uploadCompletedAtSelector = (state: { upload: { completedAt: number | null } }) =>
  state.upload.completedAt;

export const MeetingList = ({
  contactId,
  showFilter = false,
  onAttentionChange,
}: MeetingListProps) => {
  const { t, locale } = useTranslation();
  const theme = useAppTheme();
  const router = useRouter();
  const pendingUploadMeetingIds = useVoiceRecordingStore(pendingUploadMeetingIdsSelector);
  const failedUploadMeetingIds = useVoiceRecordingStore(failedUploadMeetingIdsSelector);
  const completedAt = useVoiceRecordingStore(uploadCompletedAtSelector);
  const requestMicrophonePermission = useVoiceRecordingStore(
    (state) => state.requestMicrophonePermission,
  );
  const [selectedUserId, setSelectedUserId] = useState<number | typeof FILTER_ME>(FILTER_ME);
  const [sheetVisible, setSheetVisible] = useState(false);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const { handleAccept, consentDialog, isConsentVisible } = useAiTranscriptionConsentDialog();

  const { data: meData, loading: meLoading } = useQuery(MeDocument);
  const { data: usersData, loading: usersLoading } = useQuery(GetUsersDocument, {
    variables: USER_PICKER_GET_USERS_VARIABLES,
    fetchPolicy: 'cache-and-network',
    skip: !showFilter,
  });

  const meUserId = useMemo(() => {
    const rawUserId = meData?.me.backendAppUserId;
    const parsedUserId = typeof rawUserId === 'number' ? rawUserId : Number(rawUserId);

    return Number.isFinite(parsedUserId) ? parsedUserId : null;
  }, [meData?.me.backendAppUserId]);

  const userOptions = useMemo<readonly UserOption[]>(() => {
    const users = usersData?.users.data ?? [];
    const otherUsers = users
      .filter((user) => user.id !== meUserId)
      .map((user) => ({ id: user.id, label: user.name }));

    if (meUserId == null) {
      return users.map((user) => ({ id: user.id, label: user.name }));
    }

    return [
      { id: meUserId, label: t('meetings.filter.me', { defaultValue: 'me' }) },
      ...otherUsers,
    ];
  }, [meUserId, t, usersData?.users.data]);

  const selectedSheetUserId = selectedUserId === FILTER_ME ? meUserId : selectedUserId;
  const userUid = useMemo(() => {
    if (!showFilter) {
      return contactId ? undefined : meData?.me.backendAppUserUid;
    }

    if (selectedUserId === FILTER_ME) {
      return meData?.me.backendAppUserUid;
    }

    const selectedUser = usersData?.users.data.find((user) => user.id === selectedUserId);

    return selectedUser?.uid;
  }, [contactId, meData?.me.backendAppUserUid, selectedUserId, showFilter, usersData?.users.data]);
  const shouldWaitForMe = !contactId && meLoading;

  const chipLabel = t('meetings.filter.personInCharge', { defaultValue: 'Person in charge' });

  const chipValue = useMemo(() => {
    if (selectedUserId === FILTER_ME) {
      return t('meetings.filter.me', { defaultValue: 'me' });
    }

    const selectedUser = userOptions.find((user) => user.id === selectedUserId);

    return selectedUser?.label ?? '';
  }, [selectedUserId, t, userOptions]);

  const handleOpenSheet = useCallback(() => {
    setSheetVisible(true);
  }, []);

  const handleDismissSheet = useCallback(() => {
    setSheetVisible(false);
  }, []);

  const handleSelectUser = useCallback(
    (userId: number) => {
      setSelectedUserId(userId === meUserId ? FILTER_ME : userId);
      setSheetVisible(false);
    },
    [meUserId],
  );

  const { data, loading, error, refetch, fetchMore, networkStatus } = useQuery(
    GetMeetingsDocument,
    {
      variables: {
        input: {
          page: 1,
          perPage: PER_PAGE,
          contactId,
          userUid,
        },
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip: shouldWaitForMe,
    },
  );

  const buildVariables = useMemo(
    () => (nextPage: number) => ({
      input: {
        page: nextPage,
        perPage: PER_PAGE,
        contactId,
        userUid,
      },
    }),
    [contactId, userUid],
  );

  const { isFetchingMore, handleEndReached } = usePaginatedFetchMore<GetMeetingsQuery>({
    networkStatus,
    data,
    entityKey: 'meetings',
    fetchMore,
    buildVariables,
  });

  const { onRefresh, refreshing } = useRefreshHandler(refetch);
  const hasMounted = useRef(false);

  useFocusEffect(
    useCallback(() => {
      void useVoiceRecordingStore.getState().loadPendingUploadMeetingIds();
      void useVoiceRecordingStore.getState().retryPendingUploads();

      if (!hasMounted.current) {
        hasMounted.current = true;

        return;
      }

      void refetch();
    }, [refetch]),
  );

  useEffect(() => {
    if (!completedAt) {
      return;
    }

    void refetch();
  }, [completedAt, refetch]);

  const meetings = useMemo(() => data?.meetings.data ?? [], [data]);

  const sections = useMemo<readonly MeetingSection[]>(() => {
    const groupedMeetings = new Map<string, GetMeetingsQuery['meetings']['data'][number][]>();

    meetings.forEach((meeting) => {
      const groupKey = meeting.startsAt.slice(0, 7);
      const existingItems = groupedMeetings.get(groupKey);

      if (!existingItems) {
        groupedMeetings.set(groupKey, [meeting]);

        return;
      }

      existingItems.push(meeting);
    });

    return Array.from(groupedMeetings.entries())
      .sort(([left], [right]) => right.localeCompare(left))
      .map(([, items]) => ({
        title: formatMeetingDate(items[0].startsAt, locale),
        data: items,
      }));
  }, [meetings, locale]);

  const hasAttentionItems = useMemo(
    () => meetings.some((meeting) => meeting.recording != null && meeting.attendees.length === 0),
    [meetings],
  );

  useEffect(() => {
    onAttentionChange?.(hasAttentionItems);
  }, [hasAttentionItems, onAttentionChange]);

  const handleMeetingPress = useCallback(
    (meetingId: string) => {
      router.push(`/meeting/${meetingId}`);
    },
    [router],
  );

  const handleRequestStartRecording = useCallback(() => {
    if (!meData?.me) {
      return;
    }

    handleAccept(() => {
      setShowStartDialog(true);
    });
  }, [meData?.me, handleAccept]);

  const handleConfirmStartRecording = useCallback(async () => {
    if (!meData?.me) {
      return;
    }

    setShowStartDialog(false);

    const granted = await requestMicrophonePermission();
    if (!granted) {
      return;
    }

    router.push({
      pathname: '/recording',
      params: { userId: meData.me.id, accountId: meData.me.accountId },
    });
  }, [meData, router, requestMicrophonePermission]);

  const handleDismissStartRecording = useCallback(() => {
    setShowStartDialog(false);
  }, []);

  const keyExtractor = useCallback(
    (item: GetMeetingsQuery['meetings']['data'][number]) => item.id,
    [],
  );

  const renderItem = useCallback(
    ({ item }: { item: GetMeetingsQuery['meetings']['data'][number] }) => (
      <MeetingListRow
        item={item}
        failedUploadMeetingIds={failedUploadMeetingIds}
        pendingUploadMeetingIds={pendingUploadMeetingIds}
        onPressMeeting={handleMeetingPress}
      />
    ),
    [handleMeetingPress, failedUploadMeetingIds, pendingUploadMeetingIds],
  );

  const renderSectionHeader = useCallback(
    ({
      section,
    }: {
      section: SectionListData<GetMeetingsQuery['meetings']['data'][number], MeetingSection>;
    }) => <SectionDateHeader title={section.title} />,
    [],
  );

  const listHeader = useMemo(
    () => (
      <MeetingListHeader
        chipLabel={chipLabel}
        chipValue={chipValue}
        onChipPress={handleOpenSheet}
        showFilter={showFilter}
      />
    ),
    [chipLabel, chipValue, handleOpenSheet, showFilter],
  );

  const emptyDescription = showFilter
    ? t('meetings.emptyAllDescription', {
        defaultValue:
          'There are currently no meeting memo registered for the selected person in charge.',
      })
    : t('meetings.emptyMyDescription', {
        defaultValue:
          "You don't have any meeting memos yet. Start input to create your first meeting memo.",
      });

  const emptyComponent = useMemo(
    () => (
      <EmptyState
        title={t('meetings.empty', { defaultValue: 'There are no meeting memo' })}
        description={emptyDescription}
        actionLabel={t('meetings.emptyAction', {
          defaultValue: 'Start a meeting memo input now',
        })}
        onAction={handleRequestStartRecording}
      />
    ),
    [emptyDescription, handleRequestStartRecording, t],
  );

  const footerComponent = useMemo(
    () => <PaginationFooter isFetchingMore={isFetchingMore} />,
    [isFetchingMore],
  );

  const refreshControl = useMemo(
    () => (
      <RefreshControl
        colors={[theme.colors.iconNdPrimary]}
        refreshing={refreshing}
        testID="meeting-list-refresh-control"
        tintColor={theme.colors.iconNdPrimary}
        onRefresh={onRefresh}
      />
    ),
    [onRefresh, refreshing, theme.colors.iconNdPrimary],
  );

  if ((loading || shouldWaitForMe) && !data) {
    return <LoadingState />;
  }

  if (error && !data) {
    return (
      <ErrorState message={t('meetings.loadError', { defaultValue: 'Failed to load meetings' })} />
    );
  }

  return (
    <>
      <SectionList<GetMeetingsQuery['meetings']['data'][number], MeetingSection>
        sections={sections}
        style={meetingListStyles.list}
        contentContainerStyle={
          sections.length === 0 ? meetingListStyles.emptyContent : meetingListStyles.listContent
        }
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        renderSectionHeader={renderSectionHeader}
        ItemSeparatorComponent={ItemSeparator}
        ListHeaderComponent={listHeader}
        ListEmptyComponent={emptyComponent}
        ListFooterComponent={footerComponent}
        onEndReached={handleEndReached}
        onEndReachedThreshold={0.5}
        refreshControl={refreshControl}
        stickySectionHeadersEnabled={false}
        testID="meeting-list"
      />
      {showFilter ? (
        <UserPickerSheet
          visible={sheetVisible}
          title={t('meetings.filter.selectPersonInCharge', {
            defaultValue: 'Select a person in charge',
          })}
          currentUserId={selectedSheetUserId}
          users={userOptions}
          loading={usersLoading}
          showIcons={false}
          onSelect={(userId) => (userId == null ? undefined : handleSelectUser(userId))}
          onDismiss={handleDismissSheet}
        />
      ) : null}
      {consentDialog}
      <ConfirmDialog
        visible={showStartDialog && !isConsentVisible}
        title={t('recording.startConfirmation.title')}
        body={t('recording.startConfirmation.body')}
        note={<StartRecordingNote />}
        confirmLabel={t('recording.startConfirmation.confirm')}
        dismissLabel={t('recording.startConfirmation.dismiss')}
        onConfirm={handleConfirmStartRecording}
        onDismiss={handleDismissStartRecording}
      />
    </>
  );
};

const meetingListStyles = StyleSheet.create({
  list: {
    backgroundColor: unisonColors.bgNlWeak,
  },
  listContent: {
    paddingHorizontal: spacing[4],
    paddingBottom: spacing[4],
  },
  emptyContent: {
    flexGrow: 1,
    paddingHorizontal: spacing[4],
  },
  filterRow: {
    paddingTop: spacing[4],
    flexDirection: 'row',
  },
  itemSeparator: {
    height: spacing[2],
  },
});
